import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Users, Phone, Key, Copy, Check, ArrowsClockwise, WarningCircle,
} from "@phosphor-icons/react";

/**
 * TeleproAccountsHealthPanel — VAGUE 29.
 *
 * Snapshot temps réel de tous les télépros actifs :
 * état du password, dernière connexion, appels 7j/30j, dernier appel logué.
 * Bouton "Générer magic-link 24h" en 1 clic pour les comptes qui n'ont pas
 * encore de mot de passe (crée le lien + le copie en presse-papier).
 */
export default function TeleproAccountsHealthPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/telepro/accounts-health");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Chargement impossible : " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const issueMagicLink = async (tp) => {
    try {
      const r = await api.post(`/admin/telepro/${tp.id}/magic-link`);
      const link = r.data?.magic_link;
      if (link) {
        await navigator.clipboard.writeText(link).catch(() => {});
        setCopied(tp.id);
        setTimeout(() => setCopied(""), 3000);
        toast.success(`✅ Lien copié — envoie-le à ${tp.first_name} par SMS/WhatsApp`);
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return <div className="text-sm italic text-muted-foreground">Chargement…</div>;
  }

  const needsAttention = items.filter((x) => x.needs_magic_link).length;
  const activeThisWeek = items.filter((x) => x.calls_7d > 0).length;

  return (
    <div className="space-y-4" data-testid="accounts-health-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users size={22} weight="duotone" className="text-primary" />
          <h3 className="font-display text-lg font-medium">
            État des comptes télépros
          </h3>
        </div>
        <button
          onClick={load}
          className="text-xs font-semibold px-2.5 py-1.5 rounded-sm border border-border bg-card hover:bg-primary/5 inline-flex items-center gap-1"
          data-testid="accounts-health-refresh"
        >
          <ArrowsClockwise size={12} weight="bold" /> Rafraîchir
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-card border border-border rounded-sm p-3">
          <div className="text-2xl font-bold text-primary">{items.length}</div>
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Actifs</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-3">
          <div className="text-2xl font-bold text-emerald-600">{activeThisWeek}</div>
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Ont bossé 7j</div>
        </div>
        <div className={`bg-card border rounded-sm p-3 ${needsAttention > 0 ? "border-secondary" : "border-border"}`}>
          <div className={`text-2xl font-bold ${needsAttention > 0 ? "text-secondary" : "text-muted-foreground"}`}>{needsAttention}</div>
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Sans password</div>
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-2 pl-4 sm:pl-3">Télépro</th>
              <th className="py-2">Password</th>
              <th className="py-2">Appels 7j</th>
              <th className="py-2">Appels 30j</th>
              <th className="py-2">Dernier appel</th>
              <th className="py-2">Dernier login</th>
              <th className="py-2 pr-4 sm:pr-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((tp) => (
              <tr
                key={tp.id}
                className={`border-b border-border/50 ${tp.needs_magic_link ? "bg-secondary/5" : ""}`}
                data-testid={`account-row-${tp.code}`}
              >
                <td className="py-2 pl-4 sm:pl-3">
                  <div className="font-semibold text-foreground">{tp.first_name} {tp.last_name}</div>
                  <div className="text-[11px] text-muted-foreground">{tp.code} · {tp.email}</div>
                </td>
                <td className="py-2">
                  {tp.has_password ? (
                    <span className="text-emerald-600 inline-flex items-center gap-1 text-xs">
                      <Check size={12} weight="bold" /> Défini
                    </span>
                  ) : (
                    <span className="text-secondary inline-flex items-center gap-1 text-xs font-semibold">
                      <WarningCircle size={12} weight="fill" /> Absent
                    </span>
                  )}
                </td>
                <td className="py-2">
                  <span className={`text-sm font-bold ${tp.calls_7d > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {tp.calls_7d}
                  </span>
                </td>
                <td className="py-2 text-sm text-foreground">{tp.calls_30d}</td>
                <td className="py-2 text-xs text-muted-foreground">
                  {tp.last_call_at ? tp.last_call_at.slice(0, 10) : "—"}
                </td>
                <td className="py-2 text-xs text-muted-foreground">
                  {tp.last_login_at ? tp.last_login_at.slice(0, 10) : "—"}
                </td>
                <td className="py-2 pr-4 sm:pr-3 text-right">
                  <button
                    onClick={() => issueMagicLink(tp)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm ${
                      copied === tp.id
                        ? "bg-emerald-500 text-white"
                        : "bg-primary text-primary-foreground hover:bg-[#0f3d29]"
                    }`}
                    data-testid={`account-magic-${tp.code}`}
                    title="Génère un magic-link 24h et le copie dans le presse-papier"
                  >
                    {copied === tp.id ? <Check size={12} weight="bold" /> : <Key size={12} weight="bold" />}
                    {copied === tp.id ? "Copié !" : "Magic-link"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {needsAttention > 0 && (
        <div className="bg-secondary/10 border border-secondary/40 rounded-sm p-3 text-sm">
          <strong className="text-secondary">💡 {needsAttention} télépro(s) sans mot de passe</strong>
          <div className="text-foreground/80 mt-1 leading-relaxed">
            Clique sur <strong>Magic-link</strong> pour générer un lien 24h et
            l'envoyer par SMS/WhatsApp. Ils seront invités à créer leur mot de
            passe à la première connexion.
          </div>
        </div>
      )}
    </div>
  );
}
